import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/SelfSync Logo.png";
import { useNotification } from "../../contextApi/NotificationProvider";
import signupIllustration from "../../Images/signuppage.jpg";
import {
  checkTourStatus,
  loginUser,
  resendUserOtp,
  verifyUserOtp,
} from "../../redux/ReduxSlices/authSlice";
import OtpVerify from "../Otp/OtpVerify";
import ForgotPasswordPopup from "./ForgotPasswordPopup";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dispatch = useDispatch();
  const [rememberMe, setRememberMe] = useState(false);
  const ENCRYPTION_KEY = "my_super_secret_key";
  const [emailValue, setEmailValue] = useState("");
  const [showOtpVerify, setShowOtpVerify] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loginData, setLoginData] = useState(null);
  const [openForgotPasswordPopup, setOpenForgotPasswordPopup] = useState(false);
  const { loading } = useSelector((state) => state.auth);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const decryptPassword = (encryptedPassword) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedPassword, ENCRYPTION_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error("Decryption error:", error);
      return "";
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedPassword = localStorage.getItem("rememberedPassword");

    if (savedEmail) {
      setValue("email", savedEmail);
      setRememberMe(true);
    }

    if (savedPassword) {
      const decryptedPassword = decryptPassword(savedPassword);
      if (decryptedPassword) {
        setValue("password", decryptedPassword);
      }
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    const payload = {
      email: data.email,
      password: data.password,
    };

    setLoginData(data);

    try {
      const result = await dispatch(loginUser(payload)).unwrap();

      if (result?.success) {
        if (result?.otp_verify_status === false) {
          showNotification(
            "Please verify the OTP sent to your email",
            "warning",
            5000
          );

          setShowOtpVerify(true);
          setUserId(result.user_id);
          return;
        }

        if (result?.access_token) {
          // showNotification(
          //   "Sign in successfull !",
          //   "success",
          //   1000
          // );

          if (rememberMe) {
            const encryptedPassword = CryptoJS.AES.encrypt(
              data.password,
              ENCRYPTION_KEY
            ).toString();

            localStorage.setItem("rememberedEmail", data.email);
            localStorage.setItem("rememberedPassword", encryptedPassword);
          } else {
            localStorage.removeItem("rememberedEmail");
            localStorage.removeItem("rememberedPassword");
          }

          localStorage.setItem("userType", result.user.user_type);
          window.dispatchEvent(new CustomEvent("auth:userType", { detail: result.user.user_type }));

          // console.log(result.user.user_type);


          if (result.user.user_type === "individual") {
            navigate("/individual-assessment");
          } else {
            navigate("/assessments");
          }
          await dispatch(checkTourStatus())
        } else {
          showNotification(
            result?.message || "Login failed. Please try again",
            "error",
            3000
          );
        }
        await dispatch(checkTourStatus())
      } else {
        showNotification(
          result?.message || "Login failed. Please try again",
          "error",
          3000
        );
      }
    } catch (error) {
      showNotification(
        error?.message || "An error occurred during login",
        "error",
        3000
      );
    }
  };

  const handleOtpSubmit = async (otp) => {
    try {
      const result = await dispatch(
        verifyUserOtp({ otp, user_id: userId })
      ).unwrap();

      if (result.success) {
        showNotification(
          "OTP Verified. You are now Logged In",
          "success",
          5000
        );
        setShowOtpVerify(false);
        if (loginData) {
          onSubmit(loginData);
        } else {
          showNotification(
            "Login data missing. Please try again",
            "error",
            5000
          );
        }
      } else {
        showNotification(result.message || "Invalid OTP", "error", 5000);
      }
    } catch (error) {
      showNotification(
        error || "OTP verification failed. Try again",
        "error",
        5000
      );
    }
  };

  const handleResendOtp = async (e) => {
    try {
      const result = await dispatch(
        resendUserOtp({ user_id: userId })
      ).unwrap();

      if (result.success) {
        showNotification("OTP sent to your mail id", "success", 5000);
      } else {
        showNotification(result.message || "Invalid OTP", "error", 5000);
      }
    } catch (error) {
      showNotification(
        error || "OTP verification failed. Try again",
        "error",
        5000
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <div className="md:w-1/2 bg-gradient-to-br from-purple-600 to-blue-500 p-8 flex flex-col">
        <div className="max-md:flex max-md:justify-center max-md:mb-2">
          <a
            className="bg-white w-28 flex justify-center text-center rounded-full"
            href="https://selfsync.ai/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={logo} alt="Logo" className="h-9 max-md:h-7" />
          </a>
        </div>
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex-grow flex flex-col justify-center items-center text-white"
        >
          <h1 className="text-4xl font-bold mb-4 text-center">
            Sign in to your account
          </h1>
          <p className="text-xl mb-8 text-center opacity-90">
            Access your AI-powered talent assessment dashboard
          </p>

          <div className="w-full max-w-md mb-8">
            <img
              src={signupIllustration}
              alt="Login Illustration"
              className="rounded-lg shadow-xl w-full h-auto"
            />
          </div>

          <div className="w-full max-w-md space-y-3">
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>AI-driven personality insights</span>
            </div>
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>Advanced response analysis</span>
            </div>
            <div className="flex items-center">
              <FaArrowRight className="text-green-300 mr-2" />
              <span>Empower your hiring process</span>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ x: 200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="md:w-1/2 flex items-center justify-center p-8"
      >
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-8">
            <div className="w-full flex justify-center items-center mb-4">
              <img src={logo} alt="Logo" className="h-11" />
            </div>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Enter a valid email address",
                      },
                    })}
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="off"
                    {...register("password", {
                      required: "Password is required",
                    })}
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="••••••••"
                  />
                  <div
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="text-gray-500" />
                    ) : (
                      <FaEye className="text-gray-500" />
                    )}
                  </div>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenForgotPasswordPopup(true);
                    }}
                    className="font-medium text-purple-600 hover:text-purple-500"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 btn-gradient text-white font-medium rounded-lg shadow-md transition duration-300"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Don't have an account?
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate("/sign-up")}
                  className="w-full py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  Create new account
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {showOtpVerify && (
        <OtpVerify
          onSubmit={handleOtpSubmit}
          onSubmitResend={handleResendOtp}
          onClose={() => setShowOtpVerify(false)}
        />
      )}
      {openForgotPasswordPopup && (
        <ForgotPasswordPopup
          onClose={() => setOpenForgotPasswordPopup(false)}
        />
      )}
    </div>
  );
};

export default LoginPage;
